import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../../components/ui/Layout';
import PageHeader from '../../components/ui/PageHeader';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import { IconArrowLeft } from '@tabler/icons-react';
import { callFn, fmtDate } from '../contracts/contractsApi';
import { BillingChip, relativeDays } from './customersUi';
import ActivityCharts from './ActivityCharts';
import AccountPanel, { UserActions } from './AccountPanel';
import YardPanel from './YardPanel';


export default function CustomerDetail() {
  const { orgId } = useParams();
  const navigate = useNavigate();
  const [c, setC] = useState(null);
  const [error, setError] = useState(null);
  const [userError, setUserError] = useState(null);

  const load = useCallback(() => {
    callFn('getOpsCustomer', { orgId }).then(setC).catch(e => setError(e.message));
  }, [orgId]);
  useEffect(load, [load]);

  const back = <Button variant="ghost" icon={<IconArrowLeft size={14} stroke={1.5} />} onClick={() => navigate('/customers')}>Customers</Button>;

  if (!c) {
    return (
      <Layout>
        <PageHeader title="Customer" subtitle={error || 'Loading...'} actions={back} />
      </Layout>
    );
  }

  const cutoff = Date.now() - c.activeDays * 86400000;
  const active = c.users.filter(u => u.lastActiveAt && Date.parse(u.lastActiveAt) >= cutoff).length;
  const b = c.billing;
  const adminCount = c.users.filter(u => u.role === 'org_admin').length;
  const profile = [
    ['Trading name', c.profile.tradingName], ['Company number', c.profile.companyNumber], ['VAT number', c.profile.vatNumber],
    ['Industry', c.profile.industryType], ['Email', c.profile.primaryEmail], ['Phone', c.profile.phone],
    ['Website', c.profile.website], ['Address', c.profile.address],
  ].filter(([, v]) => v);

  return (
    <Layout>
      <PageHeader title={c.name} subtitle={`Ops customer · ${c.users.length} accounts · ${active} active in the last ${c.activeDays} days`} actions={back} />
      <div style={{ padding: 28, display: 'flex', flexDirection: 'column', gap: 16, maxWidth: 1100 }}>
        <Card>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, fontSize: 12 }}>
            <Info label="Billing"><BillingChip billing={b} /></Info>
            <Info label="Customer since">{fmtDate(c.createdAt)}</Info>
            <Info label="Org code"><span style={{ fontFamily: 'var(--font-mono)' }}>{c.orgCode || '—'}</span></Info>
            <Info label="Machines">{c.machineCount ?? '—'}{b.billableMachines != null && <span style={{ color: 'var(--color-text-tertiary)' }}> ({b.billableMachines} billable)</span>}</Info>
            {b.currentPeriodEnd && <Info label="Current period ends">{fmtDate(b.currentPeriodEnd)}</Info>}
            {b.lastPaymentFailedAt && <Info label="Last failed payment"><span style={{ color: 'var(--color-danger)' }}>{fmtDate(b.lastPaymentFailedAt)}</span></Info>}
            {c.features.length > 0 && <Info label="Extra features">{c.features.join(', ')}</Info>}
            {c.linkedWorkshops.length > 0 && <Info label="Linked workshops">{c.linkedWorkshops.join(', ')}</Info>}
          </div>
          {profile.length > 0 && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, fontSize: 12, marginTop: 16, paddingTop: 16, borderTop: '0.5px solid var(--color-border-light)' }}>
              {profile.map(([l, v]) => <Info key={l} label={l} span={l === 'Address' ? 2 : 1}>{v}</Info>)}
            </div>
          )}
        </Card>

        <AccountPanel customer={c} onChanged={load} />

        <ActivityCharts orgId={orgId} />

        <YardPanel orgId={orgId} />

        <MachinesCard orgId={orgId} machines={c.machines || []} onOpen={(id) => navigate(`/customers/ops/${orgId}/machines/${id}`)} />

        <Card padding={0}>
          <div style={{ padding: '12px 16px 0', fontSize: 14 }}>Users</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1.3fr 110px 110px 330px', padding: '10px 16px', gap: 12, fontSize: 10, color: 'var(--color-text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.04em', borderBottom: '0.5px solid var(--color-border-light)' }}>
            <div>Name</div><div>Email</div><div>Last active</div><div>Joined</div><div style={{ textAlign: 'right' }}>Role and access</div>
          </div>
          {userError && <div style={{ margin: '10px 16px 0', padding: '8px 12px', background: 'var(--color-danger-light)', color: 'var(--color-danger)', borderRadius: 'var(--radius-md)', fontSize: 12 }}>{userError}</div>}
          {c.users.length === 0 && <div style={{ padding: '24px 16px', fontSize: 13, color: 'var(--color-text-tertiary)' }}>No user accounts.</div>}
          {c.users.map((u, i) => {
            const recent = u.lastActiveAt && Date.parse(u.lastActiveAt) >= cutoff;
            return (
              <div key={u.uid} style={{ display: 'grid', gridTemplateColumns: '1.2fr 1.3fr 110px 110px 330px', padding: '11px 16px', gap: 12, alignItems: 'center', fontSize: 12, borderBottom: i === c.users.length - 1 ? 'none' : '0.5px solid var(--color-border-light)' }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontWeight: 500 }}>{u.name || '—'}</div>
                  {u.siteName && <div style={{ fontSize: 11, color: 'var(--color-text-tertiary)' }}>{u.siteName}</div>}
                </div>
                <div style={{ color: 'var(--color-text-secondary)', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {u.email || '—'}
                  {u.disabled && <span style={{ color: 'var(--color-danger)', marginLeft: 6 }}>(disabled)</span>}
                  {!u.hasLogin && <span style={{ color: 'var(--color-warning)', marginLeft: 6 }}>(no login)</span>}
                </div>
                <div style={{ color: recent ? 'var(--color-success)' : 'var(--color-text-tertiary)' }}>{relativeDays(u.lastActiveAt)}</div>
                <div style={{ color: 'var(--color-text-tertiary)', fontSize: 11 }}>{fmtDate(u.joinedAt)}</div>
                <UserActions orgId={orgId} user={u} adminCount={adminCount} onChanged={() => { setUserError(null); load(); }} onError={setUserError} />
              </div>
            );
          })}
        </Card>
      </div>
    </Layout>
  );
}

function Info({ label, children, span = 1 }) {
  return (
    <div style={{ gridColumn: `span ${span}` }}>
      <div style={{ fontSize: 10, color: 'var(--color-text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 4 }}>{label}</div>
      <div>{children}</div>
    </div>
  );
}

function MachinesCard({ machines, onOpen }) {
  const [showRetired, setShowRetired] = useState(false);
  const list = machines.filter(m => showRetired || !m.retired);
  const retired = machines.filter(m => m.retired).length;
  const cols = '1.6fr 110px 1fr 90px 90px';
  return (
    <Card padding={0}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px 0' }}>
        <div style={{ fontSize: 14 }}>Machines</div>
        {retired > 0 && (
          <label style={{ fontSize: 11, color: 'var(--color-text-tertiary)', display: 'flex', gap: 6, alignItems: 'center', cursor: 'pointer' }}>
            <input type="checkbox" checked={showRetired} onChange={e => setShowRetired(e.target.checked)} /> Show {retired} sold or disposed
          </label>
        )}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: cols, padding: '10px 16px', gap: 12, fontSize: 10, color: 'var(--color-text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.04em', borderBottom: '0.5px solid var(--color-border-light)' }}>
        <div>Machine</div><div>Registration</div><div>Site</div><div>Hours</div><div>Status</div>
      </div>
      {list.length === 0 && <div style={{ padding: '24px 16px', fontSize: 13, color: 'var(--color-text-tertiary)' }}>No machines.</div>}
      {list.map((m, i) => (
        <div key={m.id} onClick={() => onOpen(m.id)}
          style={{ display: 'grid', gridTemplateColumns: cols, padding: '10px 16px', gap: 12, alignItems: 'center', fontSize: 12, cursor: 'pointer', borderBottom: i === list.length - 1 ? 'none' : '0.5px solid var(--color-border-light)', opacity: m.retired ? 0.55 : 1 }}
          onMouseEnter={e => e.currentTarget.style.background = 'var(--color-bg-secondary)'}
          onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
        >
          <div style={{ minWidth: 0 }}>
            <div style={{ fontWeight: 500 }}>{[m.make, m.model].filter(Boolean).join(' ') || 'Unnamed machine'}</div>
            <div style={{ fontSize: 11, color: 'var(--color-text-tertiary)' }}>{[m.category, m.year, m.serialNumber].filter(Boolean).join(' · ')}</div>
          </div>
          <div style={{ fontFamily: 'var(--font-mono)', fontSize: 11 }}>{m.registration || '—'}</div>
          <div style={{ color: 'var(--color-text-secondary)' }}>{m.siteName || '—'}</div>
          <div>{m.currentHours ?? '—'}</div>
          <div style={{ fontSize: 11, color: m.retired ? 'var(--color-text-tertiary)' : m.isBlocked ? 'var(--color-danger)' : 'var(--color-success)' }}>
            {m.retired ? 'Retired' : m.isBlocked ? 'Blocked' : 'Active'}
          </div>
        </div>
      ))}
    </Card>
  );
}
